import { useRef, useCallback, useEffect } from 'react';
import type { DailyScrumSharedItem } from '../../services/dailyScrumService';

interface Props {
  items: DailyScrumSharedItem[];
  type: 'NOTICE' | 'ACTION_ITEM';
  onAdd: (type: string, content: string) => void;
  onUpdate: (itemId: string, data: { content?: string; status?: string }) => void;
  onDelete: (itemId: string) => void;
}

function itemsToText(items: DailyScrumSharedItem[]): string {
  return items.map((i) => i.content).join('\n');
}

export default function SharedItemsList({
  items,
  type,
  onAdd,
  onUpdate,
  onDelete,
}: Props) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initializedRef = useRef(false);

  // 아이템이 없으면 빈 아이템 1개 자동 생성
  useEffect(() => {
    if (items.length === 0 && !initializedRef.current) {
      initializedRef.current = true;
      onAdd(type, '');
    }
  }, [items.length, type, onAdd]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const minHeight = 72; // 3줄
    el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [autoResize, items]);

  const handleChange = useCallback(
    (text: string) => {
      autoResize();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        // 첫 번째 아이템에 전체 텍스트 저장
        if (items.length > 0) {
          onUpdate(items[0].itemId, { content: text });
          // 기존 2번째 이후 아이템이 있으면 삭제
          for (let i = 1; i < items.length; i++) {
            onDelete(items[i].itemId);
          }
        }
      }, 500);
    },
    [items, onUpdate, onDelete, autoResize],
  );

  const displayText = itemsToText(items);

  return (
    <textarea
      ref={textareaRef}
      defaultValue={displayText}
      key={items[0]?.itemId ?? 'empty'}
      onChange={(e) => handleChange(e.target.value)}
      placeholder="내용을 자유롭게 입력하세요"
      rows={3}
      className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden bg-transparent"
    />
  );
}
